import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import useStepPost from '../../hooks/useStepPost';
import useBusinessNameDispatch from '../../hooks/useBusinessNameDispatch';
import { FormWrap } from '../FormContainer/FormContainer';
import InputText from '../Fields/InputText';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import { getValidationSchemaImpersonate } from '../../util/helpers/getValidationSchemaImpersonate';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
} from '../../constants/sessionStorage';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

const formValidationSchema = Yup.object({
  ...getValidationSchema({
    fields: ['BusinessName'],
  }),
});

const formValidationSchemaImpersonate = Yup.object({
  ...getValidationSchemaImpersonate({
    fields: ['BusinessName'],
  }),
});

const BusinessNameForm = ({
  applicantType,
  nextPath,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const ApplicantData = useApplicantData(applicantType);
  const { t } = useTranslation();
  const { businessNameDispatch } = useBusinessNameDispatch();
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Business name',
      accountType: accountGtmType,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Business name',
      accountType: accountGtmType,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const defaultValues = {
    BusinessName: getApplicantData(ApplicantData, 'Name'),
  };

  const { handleSubmit, control } = useForm({
    resolver: yupResolver(
      isSalesAssisted ? formValidationSchemaImpersonate : formValidationSchema,
    ),
    defaultValues,
  });

  const onSubmit = ({ BusinessName }) => {
    handleGtmEvent();
    businessNameDispatch({
      data: {
        BusinessName,
      },
      ApplicantData,
      defaultValues,
      onSuccess:
        eventType === 'Form'
          ? onSuccess
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError: () => {
        setHasError(true);
        setSaveAndCloseModalOpen(true);
      },
    });
  };

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            label={t('verifyBusinessName.businessName')}
            {...field}
            {...fieldState}
            autoFocus
          />
        )}
        name="BusinessName"
      />
      <OnBoardingCta
        type="submit"
        onClick={() => setEventType('Form')}
        title={t('common.continue')}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

BusinessNameForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default BusinessNameForm;
